/**
 * OtpFormPage — Option B: 7-step digital OTP wizard
 *
 * Route: /otp/:token  (public — no auth required)
 * Steps:
 *   0  Overview        — deal summary
 *   1  Seller Details  — display from otpData
 *   2  Property        — display from otpData
 *   3  Financial Terms — display from otpData
 *   4  Your Details    — purchaser fills in their info
 *   5  Review Agreement— full SA OTP legal text, dynamically generated
 *   6  Sign & Submit   — signature canvas + consent + submit
 * F-133
 */

import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, CheckCircle2, AlertTriangle, PenLine, ChevronRight, ChevronLeft } from 'lucide-react';
import { loadRuntimeConfig } from '../config/runtime-config';

// ─── Types ────────────────────────────────────────────────────────────────────

interface OtpData {
  seller1FullName: string;
  seller1IdNumber: string;
  seller1Capacity: string;
  seller1Address: string;
  hasSeller2: boolean;
  seller2FullName?: string;
  seller2IdNumber?: string;
  seller2Capacity?: string;
  seller2Address?: string;
  propertyType: 'conventional' | 'farm' | 'sectional';
  propertyAddress: string;
  propertySuburb: string;
  propertyCity: string;
  propertyPortion?: string;
  propertyErf?: string;
  propertyTownship?: string;
  propertyFarmName?: string;
  sectionUnitNumber?: string;
  sectionDoorNumber?: string;
  schemeName?: string;
  ssNumber?: string;
  purchasePrice: string;
  depositAmount: string;
  depositDate: string;
  bondRequired?: boolean;
  bondAmount?: string;
  bondDate?: string;
  occupationDate: string;
  occupationalRental?: string;
  agentCommissionPercent?: string;
  agentName?: string;
  agencyName?: string;
  specialConditions?: string;
  irrevocabilityDate?: string;
  irrevocabilityTime?: string;
}

interface PurchaserData {
  firstName: string;
  lastName: string;
  idNumber: string;
  email: string;
  phone: string;
  physicalAddress: string;
  maritalStatus: string;
  capacity: string;
}

type PageState = 'loading' | 'invalid' | 'expired' | 'used' | 'form' | 'submitting' | 'done' | 'error';

const EMPTY_PURCHASER: PurchaserData = {
  firstName: '', lastName: '', idNumber: '', email: '',
  phone: '', physicalAddress: '', maritalStatus: 'Single',
  capacity: 'In my personal capacity',
};

const MARITAL_OPTIONS = [
  'Single',
  'Married in community of property',
  'Married out of community of property (with accrual)',
  'Married out of community of property (without accrual)',
  'Divorced',
  'Widowed',
];

const CAPACITY_OPTIONS = [
  'In my personal capacity',
  'As trustee of a trust',
  'As director of a company',
  'As member of a close corporation',
];

const WIZARD_STEPS = [
  'Overview',
  'Seller Details',
  'Property',
  'Financial Terms',
  'Your Details',
  'Review Agreement',
  'Sign & Submit',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatZAR(value: string | undefined): string {
  if (!value) return 'R 0';
  const num = parseFloat(value.replace(/,/g, ''));
  if (isNaN(num)) return `R ${value}`;
  return `R ${num.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return iso; }
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900 text-right max-w-[60%]">{value || '—'}</span>
    </div>
  );
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-2xl border border-gray-200 shadow-sm p-5 ${className}`}>
      {children}
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{children}</h3>;
}

// ─── Agreement text generator ─────────────────────────────────────────────────

function buildAgreementText(otp: OtpData, p1: PurchaserData, p2: PurchaserData | null): string {
  const sellersText = otp.hasSeller2 && otp.seller2FullName
    ? `${otp.seller1FullName} (ID: ${otp.seller1IdNumber}), ${otp.seller1Address}; and ${otp.seller2FullName} (ID: ${otp.seller2IdNumber}), ${otp.seller2Address}`
    : `${otp.seller1FullName} (ID: ${otp.seller1IdNumber}), ${otp.seller1Address}`;

  const purchasersText = p2
    ? `${p1.firstName} ${p1.lastName} (ID: ${p1.idNumber}), ${p1.physicalAddress}, ${p1.maritalStatus}; and ${p2.firstName} ${p2.lastName} (ID: ${p2.idNumber}), ${p2.physicalAddress}, ${p2.maritalStatus}`
    : `${p1.firstName} ${p1.lastName} (ID: ${p1.idNumber}), ${p1.physicalAddress}, ${p1.maritalStatus}`;

  let propertyDesc = '';
  if (otp.propertyType === 'conventional') {
    propertyDesc = `Erf ${otp.propertyErf || '___'}${otp.propertyPortion ? `, Portion ${otp.propertyPortion}` : ''}, ${otp.propertyTownship || otp.propertySuburb}, commonly known as ${otp.propertyAddress}, ${otp.propertySuburb}, ${otp.propertyCity}`;
  } else if (otp.propertyType === 'farm') {
    propertyDesc = `${otp.propertyFarmName || 'Farm'}, situated at ${otp.propertyAddress}, ${otp.propertySuburb}, ${otp.propertyCity}`;
  } else {
    propertyDesc = `Section ${otp.sectionUnitNumber || '___'} (Door No. ${otp.sectionDoorNumber || '___'}) of the Sectional Title Scheme known as ${otp.schemeName || '___'} (SS ${otp.ssNumber || '___'}), situated at ${otp.propertyAddress}, ${otp.propertySuburb}, ${otp.propertyCity}`;
  }

  const commissionPercent = otp.agentCommissionPercent || '5.5';
  const commissionAmt = otp.purchasePrice
    ? formatZAR(String(parseFloat(otp.purchasePrice) * parseFloat(commissionPercent) / 100))
    : `${commissionPercent}% of the purchase price`;

  const bondClause = otp.bondRequired && otp.bondAmount
    ? `5. BOND\n5.1 This Agreement is subject to the Purchaser obtaining approval of a mortgage bond in the amount of ${formatZAR(otp.bondAmount)} from a registered financial institution by no later than ${formatDate(otp.bondDate)}.\n5.2 The Purchaser undertakes to apply for such bond without delay. Should the Purchaser fail to obtain approval within the stipulated period, either party shall be entitled to cancel this Agreement in writing, whereupon all monies paid shall be refunded without deduction.\n\n`
    : `5. CASH SALE\n5.1 This is a cash sale. The full purchase price shall be paid on registration of transfer into the name of the Purchaser.\n\n`;

  const occupationalRentalClause = otp.occupationalRental && parseFloat(otp.occupationalRental) > 0
    ? `An occupational rental of ${formatZAR(otp.occupationalRental)} per month shall be payable by the Purchaser to the Seller from the date of occupation until the date of registration of transfer.`
    : 'No occupational rental shall be payable.';

  const specialClause = otp.specialConditions?.trim()
    ? `13. SPECIAL CONDITIONS\n${otp.specialConditions.trim()}\n\n`
    : '';

  const irrevocabilityClause = otp.irrevocabilityDate
    ? `${formatDate(otp.irrevocabilityDate)} at ${otp.irrevocabilityTime || '17:00'}`
    : '48 hours from date of signing by the Purchaser';

  return `AGREEMENT OF SALE OF IMMOVABLE PROPERTY
("The OTP / Offer to Purchase")

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. PARTIES

1.1 SELLER(S)
${sellersText}
(hereinafter referred to as "the Seller")

1.2 PURCHASER(S)
${purchasersText}
(hereinafter referred to as "the Purchaser")

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

2. PROPERTY

2.1 The Seller hereby sells to the Purchaser, who hereby purchases the immovable property described as:

${propertyDesc}

(hereinafter referred to as "the Property")

2.2 The Seller warrants that the Property is registered in the Seller's name in the Deeds Registry and that the Seller has the legal capacity to sell same.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

3. PURCHASE PRICE

3.1 The purchase price for the Property is ${formatZAR(otp.purchasePrice)}.

3.2 The purchase price shall be paid as follows:
    (a) Deposit: ${formatZAR(otp.depositAmount)} payable on or before ${formatDate(otp.depositDate)}, to be held in trust by the transferring attorneys pending registration of transfer.
    (b) Balance: The balance of the purchase price shall be paid on registration of transfer or as otherwise stipulated herein.

3.3 All amounts payable shall be paid to the Seller's appointed transferring attorneys, who are hereby authorised to hold such amounts in their trust account.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

4. DEPOSIT

4.1 The deposit of ${formatZAR(otp.depositAmount)} shall be paid to the transferring attorneys' trust account on or before ${formatDate(otp.depositDate)}.

4.2 Should the Purchaser fail to pay the deposit timeously, the Seller shall be entitled, without notice, to cancel this Agreement and claim any damages suffered.

4.3 The deposit shall earn interest at the prevailing trust rate, which shall accrue for the benefit of the Purchaser.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${bondClause}6. OCCUPATION AND POSSESSION

6.1 Occupation of the Property shall be given to the Purchaser on ${formatDate(otp.occupationDate)} ("the Occupation Date").

6.2 ${occupationalRentalClause}

6.3 Should the Purchaser take occupation before the Occupation Date, the Purchaser shall be responsible for all municipal rates, taxes, levies, and utilities from the actual date of occupation.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

7. CONDITION OF PROPERTY (VOETSTOETS)

7.1 The Property is sold voetstoets ("as is") in its present condition and state of repair. The Purchaser declares having inspected the Property and being satisfied with its condition.

7.2 The Seller is not aware of any latent defects in the Property other than those specifically disclosed in writing herein. The Seller's warranty against latent defects is accordingly limited to defects actually known to the Seller.

7.3 The Seller shall not be liable for any patent defects or for any defects that were reasonably discoverable upon inspection of the Property.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

8. COMPLIANCE CERTIFICATES

8.1 The Seller shall, at the Seller's own cost, provide the following valid compliance certificates prior to registration of transfer:
    (a) Electrical Compliance Certificate (COC) issued by a registered electrician;
    (b) Gas Compliance Certificate, if applicable;
    (c) Electric Fence Certificate of Compliance, if applicable.

8.2 Should any defects be identified during the compliance inspection process, the Seller shall rectify such defects at the Seller's own cost prior to transfer.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

9. TRANSFER OF OWNERSHIP

9.1 Transfer of the Property shall be effected by the Seller's nominated transferring attorneys as soon as reasonably possible after all suspensive conditions (if any) have been fulfilled and all amounts payable hereunder have been paid.

9.2 The transfer costs, including conveyancing fees, Deeds Office fees, and transfer duty (if applicable), shall be for the account of the Purchaser.

9.3 The Purchaser undertakes to sign all documents and take all steps required to give effect to registration of transfer within 5 (five) business days of being requested to do so.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

10. RISK AND BENEFIT

10.1 The risk and benefit of the Property shall pass to the Purchaser on the date of occupation or the date of registration of transfer, whichever occurs first.

10.2 Until the risk passes to the Purchaser, the Seller shall maintain adequate insurance on the Property.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

11. AGENT'S COMMISSION

11.1 ${otp.agencyName || 'The Agent'} (${otp.agentName || 'Agent'}) has been the effective cause of this transaction.

11.2 The Seller shall pay commission of ${commissionPercent}% plus VAT (= ${commissionAmt} plus VAT) of the purchase price to the Agent upon registration of transfer.

11.3 The commission is payable from the proceeds of the sale and the transferring attorneys are authorised to deduct and pay the commission on the Seller's behalf upon registration.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

12. BREACH

12.1 Should either party ("the defaulting party") commit a material breach of this Agreement and fail to remedy such breach within 7 (seven) calendar days after receipt of written notice requiring it to do so, the aggrieved party shall be entitled to cancel this Agreement and/or claim damages.

12.2 Should the Purchaser default after cancellation, the Seller shall be entitled to retain the deposit as a reasonable pre-estimate of damages, without prejudice to any further claims.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${specialClause}14. GENERAL CONDITIONS

14.1 FICA COMPLIANCE: Both parties undertake to provide all documentation required in terms of the Financial Intelligence Centre Act 38 of 2001 to the transferring attorneys upon request.

14.2 ENTIRE AGREEMENT: This Agreement constitutes the entire agreement between the parties. No representations, warranties, or undertakings not contained herein shall be binding on either party.

14.3 VARIATION: No variation, amendment, or cancellation of this Agreement shall be of any force or effect unless reduced to writing and signed by both parties.

14.4 JURISDICTION: This Agreement shall be governed by and construed in accordance with the laws of the Republic of South Africa.

14.5 COSTS: Each party shall bear their own legal costs in the event of a dispute, unless a court orders otherwise.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

15. IRREVOCABILITY

15.1 This offer shall remain open for acceptance by the Seller until ${irrevocabilityClause}.

15.2 Should the Seller fail to accept this offer within the stipulated period, this Agreement shall lapse and all monies paid by the Purchaser shall be refunded without deduction or interest.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

SIGNED BY PURCHASER: ___________________________

FULL NAME: ${p1.firstName} ${p1.lastName}
ID NUMBER: ${p1.idNumber}
DATE: ___________________________
PLACE: ___________________________

${p2 ? `SIGNED BY SECOND PURCHASER: ___________________________\n\nFULL NAME: ${p2.firstName} ${p2.lastName}\nID NUMBER: ${p2.idNumber}\nDATE: ___________________________\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n` : ''}ACCEPTED BY SELLER: ___________________________

FULL NAME: ${otp.seller1FullName}
ID NUMBER: ${otp.seller1IdNumber}
DATE: ___________________________
PLACE: ___________________________
`;
}

// ─── Step progress bar ────────────────────────────────────────────────────────

function StepBar({ step }: { step: number }) {
  return (
    <div className="flex items-center gap-0 mb-8">
      {WIZARD_STEPS.map((label, i) => (
        <div key={i} className="flex items-center flex-1 last:flex-none">
          <div className="flex flex-col items-center">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
              i < step ? 'bg-indigo-600 text-white' :
              i === step ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-400' :
              'bg-gray-100 text-gray-400'
            }`}>{i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}</div>
            <span className={`text-[10px] mt-1 hidden md:block whitespace-nowrap ${i === step ? 'text-indigo-700 font-semibold' : i < step ? 'text-indigo-400' : 'text-gray-400'}`}>
              {label}
            </span>
          </div>
          {i < WIZARD_STEPS.length - 1 && (
            <div className={`flex-1 h-0.5 mx-1 ${i < step ? 'bg-indigo-400' : 'bg-gray-200'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function OtpFormPage({ token }: { token: string }) {
  const [state, setState] = useState<PageState>('loading');
  const [step, setStep] = useState(0);
  const [otpData, setOtpData] = useState<OtpData | null>(null);
  const [prefill, setPrefill] = useState<Record<string, string>>({});
  const [purchaser1, setPurchaser1] = useState<PurchaserData>({ ...EMPTY_PURCHASER });
  const [hasPurchaser2, setHasPurchaser2] = useState(false);
  const [purchaser2, setPurchaser2] = useState<PurchaserData>({ ...EMPTY_PURCHASER });
  const [agreementRead, setAgreementRead] = useState(false);
  const [consent, setConsent] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const agreementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadRuntimeConfig().then(cfg => {
      const base = cfg.apiGateway.baseUrl;
      fetch(`${base}/public/otp/${token}`)
        .then(r => r.json())
        .then(json => {
          if (!json.success) {
            const msg = json.message || '';
            if (msg.includes('expired')) setState('expired');
            else if (msg.includes('already been used')) setState('used');
            else { setErrorMsg(msg); setState('invalid'); }
            return;
          }
          const data = json.data;
          setOtpData(data.otpData || null);
          const pf = data.prefill || {};
          setPrefill(pf);
          // Pre-fill purchaser1 from client record
          setPurchaser1(prev => ({
            ...prev,
            firstName: pf.firstName || '',
            lastName: pf.lastName || '',
            idNumber: pf.idNumber || '',
            email: pf.email || '',
            phone: pf.phone || '',
          }));
          setState('form');
        })
        .catch(() => { setErrorMsg('Could not load the form. Please try again.'); setState('error'); });
    }).catch(() => { setErrorMsg('Could not load the form. Please try again.'); setState('error'); });
  }, [token]);

  // ── Canvas drawing ──
  const getPos = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setHasSigned(true);
  };

  const endDraw = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
  };

  // ── Validation ──
  const validateStep4 = (): string => {
    const p = purchaser1;
    if (!p.firstName.trim()) return 'First name is required';
    if (!p.lastName.trim()) return 'Last name is required';
    if (!p.idNumber.trim()) return 'ID / Passport number is required';
    if (!p.email.trim()) return 'Email address is required';
    if (!p.phone.trim()) return 'Contact number is required';
    if (!p.physicalAddress.trim()) return 'Physical address is required';
    if (hasPurchaser2) {
      const p2 = purchaser2;
      if (!p2.firstName.trim()) return 'Purchaser 2 first name is required';
      if (!p2.lastName.trim()) return 'Purchaser 2 last name is required';
      if (!p2.idNumber.trim()) return 'Purchaser 2 ID number is required';
    }
    return '';
  };

  const next = () => {
    if (step === 4) {
      const err = validateStep4();
      if (err) { setErrorMsg(err); return; }
    }
    setErrorMsg('');
    setStep(s => s + 1);
    window.scrollTo(0, 0);
  };

  const back = () => { setErrorMsg(''); setStep(s => s - 1); window.scrollTo(0, 0); };

  const handleSubmit = async () => {
    if (!hasSigned) { setErrorMsg('Please draw your signature'); return; }
    if (!consent) { setErrorMsg('Please tick the consent checkbox'); return; }

    setState('submitting');
    setErrorMsg('');
    const signatureDataUrl = canvasRef.current?.toDataURL('image/png') || '';

    try {
      const cfg = await loadRuntimeConfig();
      const base = cfg.apiGateway.baseUrl;
      const res = await fetch(`${base}/public/otp/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          purchaser1,
          purchaser2: hasPurchaser2 ? purchaser2 : null,
          signatureDataUrl,
          consentChecked: true,
          formSnapshot: { otpData, purchaser1, purchaser2: hasPurchaser2 ? purchaser2 : null },
        }),
      });
      const json = await res.json();
      if (json.success) {
        setState('done');
      } else {
        setErrorMsg(json.message || 'Submission failed. Please try again.');
        setState('form');
      }
    } catch {
      setErrorMsg('Submission failed. Please check your connection and try again.');
      setState('form');
    }
  };

  // ─── Terminal states ───────────────────────────────────────────────────────

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (state === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Link Expired</h1>
          <p className="text-gray-600">This link has expired. Please contact your agent to receive a new one.</p>
        </div>
      </div>
    );
  }

  if (state === 'used') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Already Submitted</h1>
          <p className="text-gray-600">This OTP has already been submitted. Please contact your agent if you need assistance.</p>
        </div>
      </div>
    );
  }

  if (state === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2 text-gray-900">OTP Submitted Successfully</h1>
          <p className="text-gray-600">Thank you. Your signed Offer to Purchase has been submitted. Your agent will contact you to confirm next steps.</p>
        </div>
      </div>
    );
  }

  if (state === 'invalid' || state === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Invalid Link</h1>
          <p className="text-gray-600">{errorMsg || 'This link is not valid. Please contact your agent.'}</p>
        </div>
      </div>
    );
  }

  // ─── Wizard ────────────────────────────────────────────────────────────────

  const otp = otpData;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-block bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
            Something Property
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Offer to Purchase</h1>
          <p className="text-gray-500 text-sm mt-1">Digital Agreement — Step {step + 1} of {WIZARD_STEPS.length}</p>
        </div>

        <StepBar step={step} />

        {/* ── Step 0: Overview ─────────────────────────────────────────────── */}
        {step === 0 && (
          <div className="space-y-4">
            <Card className="bg-gradient-to-br from-indigo-600 to-indigo-700 text-white border-0">
              <p className="text-indigo-200 text-xs font-semibold uppercase tracking-wide mb-1">Property For Sale</p>
              <h2 className="text-xl font-bold">{otp?.propertyAddress || '—'}</h2>
              <p className="text-indigo-200 mt-0.5">{otp?.propertySuburb}, {otp?.propertyCity}</p>
              <div className="mt-4 pt-4 border-t border-indigo-500">
                <p className="text-indigo-200 text-xs">Purchase Price</p>
                <p className="text-2xl font-bold">{formatZAR(otp?.purchasePrice)}</p>
              </div>
            </Card>

            <Card>
              <SectionHeading>What you're signing</SectionHeading>
              <p className="text-sm text-gray-600 leading-relaxed">
                You have been invited to review and sign an <strong>Offer to Purchase (OTP)</strong> — a legally binding
                Agreement of Sale for the property above. This wizard will guide you through:
              </p>
              <ul className="mt-3 space-y-2">
                {['Review the seller and property details', 'Confirm the financial terms', 'Fill in your personal details', 'Read the full legal agreement', 'Sign digitally'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <div className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</div>
                    {item}
                  </li>
                ))}
              </ul>
            </Card>

            <Card>
              <SectionHeading>Selling Agent</SectionHeading>
              <InfoRow label="Agent" value={otp?.agentName || '—'} />
              <InfoRow label="Agency" value={otp?.agencyName || '—'} />
            </Card>
          </div>
        )}

        {/* ── Step 1: Seller Details ────────────────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-4">
            <Card>
              <SectionHeading>Seller 1</SectionHeading>
              <InfoRow label="Full Name" value={otp?.seller1FullName || '—'} />
              <InfoRow label="ID / Passport Number" value={otp?.seller1IdNumber || '—'} />
              <InfoRow label="Capacity" value={otp?.seller1Capacity || '—'} />
              <InfoRow label="Address" value={otp?.seller1Address || '—'} />
            </Card>

            {otp?.hasSeller2 && otp.seller2FullName && (
              <Card>
                <SectionHeading>Seller 2</SectionHeading>
                <InfoRow label="Full Name" value={otp.seller2FullName} />
                <InfoRow label="ID / Passport Number" value={otp.seller2IdNumber || '—'} />
                <InfoRow label="Capacity" value={otp.seller2Capacity || '—'} />
                <InfoRow label="Address" value={otp.seller2Address || '—'} />
              </Card>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
              Please verify these details are correct. If anything looks wrong, contact your agent before proceeding.
            </div>
          </div>
        )}

        {/* ── Step 2: Property Details ──────────────────────────────────────── */}
        {step === 2 && (
          <div className="space-y-4">
            <Card>
              <SectionHeading>Property Description</SectionHeading>
              <InfoRow label="Type" value={
                otp?.propertyType === 'conventional' ? 'Conventional (Freehold)' :
                otp?.propertyType === 'farm' ? 'Farm' : 'Sectional Title'
              } />
              <InfoRow label="Address" value={otp?.propertyAddress || '—'} />
              <InfoRow label="Suburb" value={otp?.propertySuburb || '—'} />
              <InfoRow label="City" value={otp?.propertyCity || '—'} />
            </Card>

            {otp?.propertyType === 'conventional' && (otp.propertyErf || otp.propertyPortion || otp.propertyTownship) && (
              <Card>
                <SectionHeading>Legal Description</SectionHeading>
                {otp.propertyErf && <InfoRow label="Erf Number" value={otp.propertyErf} />}
                {otp.propertyPortion && <InfoRow label="Portion" value={otp.propertyPortion} />}
                {otp.propertyTownship && <InfoRow label="Township" value={otp.propertyTownship} />}
              </Card>
            )}

            {otp?.propertyType === 'farm' && otp.propertyFarmName && (
              <Card>
                <SectionHeading>Farm Details</SectionHeading>
                <InfoRow label="Farm Name" value={otp.propertyFarmName} />
              </Card>
            )}

            {otp?.propertyType === 'sectional' && (
              <Card>
                <SectionHeading>Sectional Title Details</SectionHeading>
                {otp.sectionUnitNumber && <InfoRow label="Unit Number" value={otp.sectionUnitNumber} />}
                {otp.sectionDoorNumber && <InfoRow label="Door Number" value={otp.sectionDoorNumber} />}
                {otp.schemeName && <InfoRow label="Scheme Name" value={otp.schemeName} />}
                {otp.ssNumber && <InfoRow label="SS Number" value={otp.ssNumber} />}
              </Card>
            )}
          </div>
        )}

        {/* ── Step 3: Financial Terms ───────────────────────────────────────── */}
        {step === 3 && (
          <div className="space-y-4">
            <Card>
              <SectionHeading>Purchase Price</SectionHeading>
              <InfoRow label="Purchase Price" value={formatZAR(otp?.purchasePrice)} />
            </Card>

            <Card>
              <SectionHeading>Deposit</SectionHeading>
              <InfoRow label="Deposit Amount" value={formatZAR(otp?.depositAmount)} />
              <InfoRow label="Due Date" value={formatDate(otp?.depositDate)} />
            </Card>

            {otp?.bondRequired && otp.bondAmount && (
              <Card>
                <SectionHeading>Bond</SectionHeading>
                <InfoRow label="Bond Amount" value={formatZAR(otp.bondAmount)} />
                <InfoRow label="Approval Deadline" value={formatDate(otp.bondDate)} />
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                  This sale is subject to bond approval by the date above.
                </div>
              </Card>
            )}

            <Card>
              <SectionHeading>Occupation</SectionHeading>
              <InfoRow label="Occupation Date" value={formatDate(otp?.occupationDate)} />
              {otp?.occupationalRental && parseFloat(otp.occupationalRental) > 0 && (
                <InfoRow label="Occupational Rental" value={`${formatZAR(otp.occupationalRental)}/month`} />
              )}
            </Card>

            {otp?.irrevocabilityDate && (
              <Card>
                <SectionHeading>Offer Validity</SectionHeading>
                <InfoRow label="Irrevocability Date" value={formatDate(otp.irrevocabilityDate)} />
                {otp.irrevocabilityTime && <InfoRow label="Time" value={otp.irrevocabilityTime} />}
              </Card>
            )}
          </div>
        )}

        {/* ── Step 4: Purchaser Details ─────────────────────────────────────── */}
        {step === 4 && (
          <div className="space-y-4">
            <Card>
              <SectionHeading>Your Details (Purchaser 1)</SectionHeading>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">First Name *</label>
                    <Input value={purchaser1.firstName} onChange={e => setPurchaser1(p => ({ ...p, firstName: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Last Name *</label>
                    <Input value={purchaser1.lastName} onChange={e => setPurchaser1(p => ({ ...p, lastName: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">ID / Passport Number *</label>
                  <Input value={purchaser1.idNumber} onChange={e => setPurchaser1(p => ({ ...p, idNumber: e.target.value }))} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Email Address *</label>
                    <Input type="email" value={purchaser1.email} onChange={e => setPurchaser1(p => ({ ...p, email: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Contact Number *</label>
                    <Input type="tel" value={purchaser1.phone} onChange={e => setPurchaser1(p => ({ ...p, phone: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Physical Address *</label>
                  <Input value={purchaser1.physicalAddress} onChange={e => setPurchaser1(p => ({ ...p, physicalAddress: e.target.value }))} placeholder="Street, Suburb, City" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Marital Status</label>
                  <select
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    value={purchaser1.maritalStatus}
                    onChange={e => setPurchaser1(p => ({ ...p, maritalStatus: e.target.value }))}
                  >
                    {MARITAL_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Purchasing Capacity</label>
                  <select
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    value={purchaser1.capacity}
                    onChange={e => setPurchaser1(p => ({ ...p, capacity: e.target.value }))}
                  >
                    {CAPACITY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </Card>

            <label className="flex items-center gap-2 cursor-pointer px-1">
              <input type="checkbox" checked={hasPurchaser2} onChange={e => setHasPurchaser2(e.target.checked)} className="w-4 h-4" />
              <span className="text-sm text-gray-700">Add a second purchaser to this agreement</span>
            </label>

            {hasPurchaser2 && (
              <Card>
                <SectionHeading>Purchaser 2</SectionHeading>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">First Name *</label>
                      <Input value={purchaser2.firstName} onChange={e => setPurchaser2(p => ({ ...p, firstName: e.target.value }))} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Last Name *</label>
                      <Input value={purchaser2.lastName} onChange={e => setPurchaser2(p => ({ ...p, lastName: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">ID / Passport Number *</label>
                    <Input value={purchaser2.idNumber} onChange={e => setPurchaser2(p => ({ ...p, idNumber: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Email</label>
                      <Input type="email" value={purchaser2.email} onChange={e => setPurchaser2(p => ({ ...p, email: e.target.value }))} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Phone</label>
                      <Input type="tel" value={purchaser2.phone} onChange={e => setPurchaser2(p => ({ ...p, phone: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Physical Address</label>
                    <Input value={purchaser2.physicalAddress} onChange={e => setPurchaser2(p => ({ ...p, physicalAddress: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Marital Status</label>
                    <select
                      className="w-full border rounded-md px-3 py-2 text-sm"
                      value={purchaser2.maritalStatus}
                      onChange={e => setPurchaser2(p => ({ ...p, maritalStatus: e.target.value }))}
                    >
                      {MARITAL_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* ── Step 5: Review Agreement ──────────────────────────────────────── */}
        {step === 5 && (
          <div className="space-y-4">
            <Card>
              <p className="text-sm text-gray-600 mb-3">
                Please read the full Offer to Purchase agreement below before signing. Scroll to the bottom to confirm you have read it.
              </p>
              <div
                ref={agreementRef}
                className="bg-gray-50 border border-gray-200 rounded-xl p-4 max-h-[55vh] overflow-y-auto"
                onScroll={e => {
                  const el = e.currentTarget;
                  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) setAgreementRead(true);
                }}
              >
                <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                  {otp ? buildAgreementText(otp, purchaser1, hasPurchaser2 ? purchaser2 : null) : ''}
                </pre>
              </div>
            </Card>

            {!agreementRead && (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                Please scroll to the bottom of the agreement to continue.
              </div>
            )}

            {agreementRead && (
              <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> You have read the full agreement. You may now proceed to sign.
              </div>
            )}
          </div>
        )}

        {/* ── Step 6: Sign & Submit ─────────────────────────────────────────── */}
        {step === 6 && (
          <div className="space-y-4">
            <Card>
              <SectionHeading>Your Signature</SectionHeading>
              <p className="text-sm text-gray-600 mb-3">
                Sign in the box below using your finger (mobile) or mouse.
              </p>
              <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-gray-50 relative">
                {!hasSigned && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                      <PenLine className="w-4 h-4" /> Draw your signature here
                    </div>
                  </div>
                )}
                <canvas
                  ref={canvasRef}
                  width={560}
                  height={180}
                  className="w-full touch-none cursor-crosshair"
                  onMouseDown={startDraw}
                  onMouseMove={draw}
                  onMouseUp={endDraw}
                  onMouseLeave={endDraw}
                  onTouchStart={e => { e.preventDefault(); startDraw(e); }}
                  onTouchMove={e => { e.preventDefault(); draw(e); }}
                  onTouchEnd={endDraw}
                />
              </div>
              <button
                type="button"
                onClick={clearSignature}
                className="text-xs text-gray-500 hover:text-gray-700 mt-2"
              >
                Clear signature
              </button>
            </Card>

            <Card>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={e => setConsent(e.target.checked)}
                  className="mt-0.5 w-4 h-4 flex-shrink-0"
                  disabled={state === 'submitting'}
                />
                <span className="text-sm text-gray-700">
                  I, <strong>{purchaser1.firstName} {purchaser1.lastName}</strong>, confirm that:
                  <ul className="mt-2 space-y-1 list-disc list-inside text-gray-600">
                    <li>I have read and understood the full Offer to Purchase agreement</li>
                    <li>The above is my electronic signature and I intend it to be legally binding</li>
                    <li>The information I have provided is true and correct</li>
                    <li>I have the legal capacity to enter into this agreement</li>
                  </ul>
                </span>
              </label>
            </Card>

            {errorMsg && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {errorMsg}
              </p>
            )}

            <Button
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-12"
              disabled={state === 'submitting' || !consent || !hasSigned}
              onClick={handleSubmit}
            >
              {state === 'submitting' ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
              ) : (
                'Sign & Submit OTP'
              )}
            </Button>
          </div>
        )}

        {/* ── Error message (non-step-6 validation) ──────────────────────── */}
        {errorMsg && step !== 6 && (
          <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {errorMsg}
          </p>
        )}

        {/* ── Navigation buttons ──────────────────────────────────────────── */}
        <div className="flex gap-3 mt-6">
          {step > 0 && (
            <Button
              variant="outline"
              onClick={back}
              disabled={state === 'submitting'}
              className="flex-none"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
          {step < WIZARD_STEPS.length - 1 && (
            <Button
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={next}
              disabled={step === 5 && !agreementRead}
            >
              {step === 0 ? "Get Started" : "Continue"} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-8">
          This digital OTP is secured by Something Property. Your data is encrypted and stored securely.
        </p>
      </div>
    </div>
  );
}
